import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { CalendarMonth, Schedule } from '@mui/icons-material';

const QuoteDetails = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Appointment scheduling state
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(new Date());
  const [appointmentDuration, setAppointmentDuration] = useState(2); // Default 2 hours
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [schedulingSuccess, setSchedulingSuccess] = useState(false);
  const [schedulingError, setSchedulingError] = useState('');

  useEffect(() => {
    const fetchQuoteDetails = async () => {
      try {
        setLoading(true);
        // Use the getQuote endpoint from the api service
        const response = await api.getQuote(id);
        const quoteData = response.data;
        
        // Create breakdown from materials_cost and labor_cost if available
        quoteData.breakdown = {
          materials: quoteData.materials_cost || quoteData.amount * 0.4,
          labor: quoteData.labor_cost || quoteData.amount * 0.6
        };
        
        setQuote(quoteData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch quote details: ' + err.message);
        setLoading(false);
      }
    };

    fetchQuoteDetails();
  }, [id]);

  const handleAcceptQuote = async () => {
    try {
      // Use the acceptQuote endpoint from the api service
      await api.acceptQuote(id);
      
      // Update the quote status locally
      setQuote({
        ...quote,
        status: 'accepted'
      });
      
      // Clear any previous errors
      setError('');
    } catch (err) {
      setError('Failed to accept quote: ' + err.message);
    }
  };

  const handleRejectQuote = async () => {
    try {
      // Use the rejectQuote endpoint from the api service
      await api.rejectQuote(id);
      
      // Update the quote status locally
      setQuote({
        ...quote,
        status: 'rejected'
      });
      
      // Clear any previous errors
      setError('');
    } catch (err) {
      setError('Failed to reject quote: ' + err.message);
    }
  };

  const handleDownloadPdf = () => {
    const pdfUrl = `${api.instance.defaults.baseURL}/api/quotes/${id}/pdf`;
    window.open(pdfUrl, '_blank');
  };
  
  const handleOpenScheduleDialog = () => {
    setOpenScheduleDialog(true);
  };
  
  const handleCloseScheduleDialog = () => {
    setOpenScheduleDialog(false);
    setSchedulingError('');
  };
  
  const handleScheduleAppointment = async () => {
    try {
      // Calculate end time based on duration
      const endTime = new Date(appointmentDate);
      endTime.setHours(endTime.getHours() + appointmentDuration);
      
      const appointmentData = {
        provider_id: quote.provider_id || 'default-provider', // Replace with actual provider ID if available
        customer_id: quote.customer_id,
        service_type: quote.job_type,
        start_time: appointmentDate.toISOString(),
        end_time: endTime.toISOString(),
        quote_id: quote.id,
        location: quote.address,
        notes: appointmentNotes
      };
      
      await api.createAppointment(appointmentData);
      
      // Close dialog and show success message
      setOpenScheduleDialog(false);
      setSchedulingSuccess(true);
      
      // Clear any previous errors
      setSchedulingError('');
    } catch (err) {
      console.error('Failed to schedule appointment:', err);
      setSchedulingError('Failed to schedule appointment: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'info';
      case 'completed':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getJobTypeLabel = (jobType) => {
    const jobTypes = {
      'painting': 'Painting',
      'flooring': 'Flooring',
      'renovation': 'Renovation',
      'plumbing': 'Plumbing',
      'electrical': 'Electrical',
      'roofing': 'Roofing',
      'landscaping': 'Landscaping'
    };
    
    return jobTypes[jobType] || jobType;
  };

  const renderQuoteContent = (text) => {
    if (!text) return null;
    
    // For the modified format (plain text with * as bullet points)
    return (
      <Box sx={{ whiteSpace: 'pre-line' }}>
        {text}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Quote Details</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : quote ? (
        <>
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="h6" gutterBottom>
                    {getJobTypeLabel(quote.job_type)} - {quote.square_feet} sq ft
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {quote.address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requested on {formatDate(quote.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    ${quote.amount ? quote.amount.toLocaleString() : '0.00'}
                  </Typography>
                  <Chip 
                    label={quote.status ? quote.status.charAt(0).toUpperCase() + quote.status.slice(1) : 'Pending'} 
                    color={getStatusColor(quote.status)}
                  />
                </Grid>
              </Grid>
              
              {quote.status === 'pending' && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="error"
                    onClick={handleRejectQuote}
                  >
                    Reject Quote
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleAcceptQuote}
                  >
                    Accept Quote
                  </Button>
                </Box>
              )}
              
              {quote.status === 'accepted' && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    variant="contained" 
                    color="success"
                    startIcon={<Schedule />}
                    onClick={handleOpenScheduleDialog}
                  >
                    Schedule Appointment
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                Quote Details
              </Typography>
              <Paper variant="outlined" sx={{ p: 3 }}>
                {renderQuoteContent(quote.description)}
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="h5" gutterBottom>
                Cost Summary
              </Typography>
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Materials</TableCell>
                        <TableCell align="right">${quote.breakdown.materials.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Labor</TableCell>
                        <TableCell align="right">${quote.breakdown.labor.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${quote.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              
              <Button 
                fullWidth 
                variant="contained" 
                color="primary"
                sx={{ mb: 2 }}
                onClick={handleDownloadPdf}
              >
                Download PDF
              </Button>
              
              <Button 
                fullWidth 
                variant="outlined" 
                color="primary"
              >
                Contact Us
              </Button>
              
              {quote.status === 'accepted' && (
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="success"
                  startIcon={<Schedule />}
                  onClick={handleOpenScheduleDialog}
                  sx={{ mt: 2 }}
                >
                  Schedule Appointment
                </Button>
              )}
            </Grid>
          </Grid>
        </>
      ) : (
        <Alert severity="info">
          Quote not found.
        </Alert>
      )}
      
      {/* Appointment Scheduling Dialog */}
      <Dialog open={openScheduleDialog} onClose={handleCloseScheduleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Your Appointment</DialogTitle>
        <DialogContent>
          {schedulingError && (
            <Alert severity="error" sx={{ mb: 2 }}>{schedulingError}</Alert>
          )}
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2 }}>
              <DateTimePicker
                label="Appointment Date & Time"
                value={appointmentDate}
                onChange={(newValue) => setAppointmentDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDate={new Date()}
                sx={{ mb: 3 }}
              />
            </Box>
          </LocalizationProvider>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="duration-label">Appointment Duration</InputLabel>
            <Select
              labelId="duration-label"
              value={appointmentDuration}
              label="Appointment Duration"
              onChange={(e) => setAppointmentDuration(e.target.value)}
            >
              <MenuItem value={1}>1 hour</MenuItem>
              <MenuItem value={2}>2 hours</MenuItem>
              <MenuItem value={3}>3 hours</MenuItem>
              <MenuItem value={4}>4 hours</MenuItem>
              <MenuItem value={6}>6 hours</MenuItem>
              <MenuItem value={8}>8 hours</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="Additional Notes"
            multiline
            rows={4}
            value={appointmentNotes}
            onChange={(e) => setAppointmentNotes(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Any special instructions or information for the service provider"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
          <Button onClick={handleScheduleAppointment} variant="contained" color="primary">
            Schedule & Send Confirmation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar
        open={schedulingSuccess}
        autoHideDuration={6000}
        onClose={() => setSchedulingSuccess(false)}
        message="Appointment scheduled successfully! Confirmation has been sent."
      />
    </Box>
  );
};

export default QuoteDetails;
